import React, { useEffect, useRef, useState } from "react"

import { trpc } from "../lib/trpc"

interface Project {
  id: string
  title: string
  description?: string
  parentId?: string
  displayName: string
  level: number
}

interface ProjectSelectorProps {
  currentProjectId: string
  taskId: string
  taskBreadcrumbs: { id: string; title: string }[]
  isFromDifferentProject?: boolean
  onProjectChange: (taskId: string, newProjectId: string) => void
  onProjectSelect: (projectId: string) => void
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  currentProjectId,
  taskId,
  taskBreadcrumbs,
  isFromDifferentProject = false,
  onProjectChange,
  onProjectSelect,
}) => {
  const [showSelector, setShowSelector] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const selectorRef = useRef<HTMLDivElement>(null)

  // Get all available projects
  const { data: allProjects, isLoading } = trpc.brain.getAllProjects.useQuery()

  // Get the trpc utils for invalidating queries
  const utils = trpc.useUtils()

  // Recategorize task mutation
  const recategorizeTaskMutation = trpc.brain.recategorizeTask.useMutation({
    onSuccess: (updatedTask) => {
      setShowSelector(false)
      // Invalidate and refetch relevant queries
      void utils.brain.getProjectTasks.invalidate()
      void utils.brain.getProjectHierarchy.invalidate()
      void utils.brain.searchContent.invalidate()

      // Call the parent callback to notify of the change
      if (updatedTask) {
        onProjectChange(taskId, updatedTask.projectId)
      }
    },
    onError: (error) => {
      console.error("Failed to move task:", error)
      // You could add a toast notification here
    },
  })

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Filter projects based on search term
  const filteredProjects =
    allProjects?.projects?.filter(
      (project) =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || []

  const handleProjectSelect = (projectId: string) => {
    if (projectId !== currentProjectId) {
      // Move the task to the new project
      recategorizeTaskMutation.mutate({
        taskId,
        newProjectId: projectId,
      })
    } else {
      // Just navigate to the project
      onProjectSelect(projectId)
      setShowSelector(false)
    }
  }

  const handleBreadcrumbClick = (projectId: string) => {
    onProjectSelect(projectId)
  }

  return (
    <div className="relative">
      {/* Project breadcrumbs with click to open selector */}
      <div className="flex items-center flex-wrap gap-1">
        {taskBreadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={breadcrumb.id}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (index === taskBreadcrumbs.length - 1) {
                  // Last breadcrumb - open selector
                  setShowSelector(!showSelector)
                } else {
                  // Navigate to parent project
                  handleBreadcrumbClick(breadcrumb.id)
                }
              }}
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                isFromDifferentProject ? "bg-blue-100 text-blue-800 hover:bg-blue-200" : "bg-foregroundx text-primary "
              } ${index === taskBreadcrumbs.length - 1 ? "ring-1 ring-gray-300" : ""}`}
              title={
                index === taskBreadcrumbs.length - 1
                  ? `Change project for this task (currently in ${breadcrumb.title})`
                  : `Switch to ${breadcrumb.title} project`
              }
            >
              📁 {breadcrumb.title}
              {index === taskBreadcrumbs.length - 1 && (
                <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
            {index < taskBreadcrumbs.length - 1 && <span className="text-gray-400 text-xs">›</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Project selector popover */}
      {showSelector && (
        <div
          ref={selectorRef}
          className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-80 max-h-96 overflow-hidden"
        >
          <div className="space-y-3">
            <div className="text-sm font-medium text-primary">Move task to project:</div>

            {/* Search input */}
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />

            {/* Project list */}
            <div className="max-h-64 overflow-y-auto space-y-1">
              {isLoading ? (
                <div className="text-sm text-gray-500 text-center py-4">Loading projects...</div>
              ) : filteredProjects.length > 0 ? (
                filteredProjects.map((project) => {
                  const isCurrentProject = project.id === currentProjectId
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleProjectSelect(project.id)}
                      disabled={recategorizeTaskMutation.isLoading}
                      className={`w-full text-left p-2 rounded-lg transition-colors  disabled:opacity-50 ${
                        isCurrentProject ? "bg-blue-50 border border-blue-200" : "border border-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-sm font-medium  truncate"
                            style={{ paddingLeft: `${project.level * 12}px` }}
                          >
                            {project.level > 0 && "└ "}
                            {project.title}
                            {isCurrentProject && <span className="ml-2 text-xs ">(current)</span>}
                          </div>
                          {project.description && (
                            <div
                              className="text-xs text-gray-500 truncate mt-0.5"
                              style={{ paddingLeft: `${project.level * 12 + (project.level > 0 ? 16 : 0)}px` }}
                            >
                              {project.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  {searchTerm ? "No projects found matching your search" : "No projects available"}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <button
                onClick={() => setShowSelector(false)}
                className="px-3 py-1.5 text-sm  hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              {recategorizeTaskMutation.isLoading && (
                <div className="flex items-center gap-2 text-sm ">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  Moving task...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectSelector
